import React from "react";
import { useI18n } from "../../i18n";
import { formatAmount } from "../utils/money";

/**
 * Ticket breakdown.
 *
 * `compact` is for the side panel, where the pinned complete button already
 * carries the total in large type: repeating it there would only cost the
 * ticket lines vertical space.
 */
const TicketTotals = ({ totals, currency, compact = false }) => {
  const { t } = useI18n();
  const applied = totals.offersApplied?.length || 0;

  return (
    <div
      className={`px-4 border-t border-slate-800 space-y-1.5 ${
        compact ? "py-2.5" : "py-3"
      }`}
    >
      <div className="flex items-center justify-between text-sm">
        <span className="text-slate-400">
          {t("pos.ticket.subtotal", {
            items: t("common.items", { count: totals.itemCount }),
          })}
        </span>
        <span className="text-slate-200 font-semibold tabular-nums">
          {formatAmount(totals.subtotal)}
        </span>
      </div>

      {/* The shop's own promotions, named, so nobody has to guess where the
          money went — and shown apart from anything the cashier knocked off */}
      {totals.offerDiscount > 0 && (
        <div className="flex items-center justify-between text-sm">
          <span className="text-amber-400 truncate pe-2">
            {applied === 1
              ? totals.offersApplied[0].title
              : t("pos.ticket.offers", { count: applied })}
          </span>
          <span className="text-amber-400 font-semibold tabular-nums">
            −{formatAmount(totals.offerDiscount)}
          </span>
        </div>
      )}

      {totals.manualDiscount > 0 && (
        <div className="flex items-center justify-between text-sm">
          <span className="text-slate-400">{t("pos.ticket.discount")}</span>
          <span className="text-amber-400 font-semibold tabular-nums">
            −{formatAmount(totals.manualDiscount)}
          </span>
        </div>
      )}

      {!compact && (
        <div className="flex items-baseline justify-between pt-1.5 border-t border-slate-800">
          <span className="text-sm font-bold uppercase tracking-wide text-slate-300">
            {t("pos.ticket.total")}
          </span>
          <span className="text-2xl font-extrabold text-emerald-400 tabular-nums">
            {formatAmount(totals.total)}
            <span className="ms-1.5 text-sm font-semibold text-slate-400">
              {currency}
            </span>
          </span>
        </div>
      )}
    </div>
  );
};

export default TicketTotals;
